import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DadosEmpresaService } from 'src/app/services/dados-empresa.service';
import { ActivatedRoute, Router } from '@angular/router';
import { take, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { DomUtilsService } from '@utils/DomUtilsService';
import { CustomErrorHandler } from '@utils/custom-error-handler';
import { requiredNaoEspacos } from '@pages/utils/endereco-form.base';
import { PageLayoutComponent } from '../../components/page-layout/page-layout.component';
import { EnderecoFormComponent } from '../../components/endereco-form/endereco-form.component';
import { NgClass } from '@angular/common';

@Component({
    selector: 'app-editar-endereco',
    templateUrl: './editar-endereco.component.html',
    styleUrls: [],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [PageLayoutComponent, EnderecoFormComponent, NgClass]
})
export class EditarEnderecoComponent implements OnInit {

  public idEmpresaLogada = localStorage.idEmpresaLogada;
  public idEndereco: string;
  public formulario: UntypedFormGroup;
  public dadosEndereco: any = null;
  public formularioChanges: boolean = false;

  // VARIAVEIS DOS ALERTS
  public alertLoading: boolean = false;
  public alertSucesso: boolean = false;
  public alertError: boolean = false;
  public mensagemErro: string = '';
  /** Indica se o endereço carregado possui logradouro e bairro genéricos */
  public cepGenerico: boolean = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly formularioBuilder: UntypedFormBuilder,
    private readonly service: DadosEmpresaService,
    public rota: Router,
    public rotaAtiva: ActivatedRoute,
    private readonly domUtils: DomUtilsService
  ) { }

  ngOnInit(): void {
    this.rotaAtiva.queryParams.pipe(take(1)).subscribe((data: any) => {
      this.idEndereco = data.idEndereco;
      this.consultaEndereco(data.idEndereco);
    });
  }

  consultaEndereco(idEndereco: string) {
    this.service.consultaDadosEndereco(this.idEmpresaLogada, idEndereco).pipe(take(1)).subscribe({
      next: (response: any) => {
        if (response.status === 'OK') {
          this.dadosEndereco = response.response;

          const logradouroVazio = !this.dadosEndereco.descricaoLogradouro?.trim();
          const bairroVazio = !this.dadosEndereco.codigoBairro?.trim();

          this.cepGenerico = logradouroVazio || bairroVazio;
          this.constroiFormulario(this.cepGenerico);
        } else {
          this.dadosEndereco = false;
        }
      },
      error: (erro: any) => {
        console.error(erro);
        this.dadosEndereco = false;
      }
    });
  }

  constroiFormulario(statusCep: boolean) {
    this.formulario = this.formularioBuilder.group({
      'codigoCep': [this.dadosEndereco.codigoCep, Validators.compose([Validators.required])],
      'descricaoLogradouro': [this.dadosEndereco.descricaoLogradouro, Validators.compose([Validators.required, requiredNaoEspacos])],
      'codigoNumero': [this.dadosEndereco.codigoNumero, Validators.compose([Validators.required, requiredNaoEspacos])],
      'codigoComplemento': [this.dadosEndereco.codigoComplemento],
      'codigoBairro': [this.dadosEndereco.codigoBairro, !this.dadosEndereco.codigoBairro?.trim() ? Validators.compose([Validators.required, requiredNaoEspacos]) : []],
      'codigoCidade': [this.dadosEndereco.codigoCidade, Validators.compose([Validators.required])],
      'codigoEstado': [this.dadosEndereco.codigoEstado, Validators.compose([Validators.required])],
      'codigoTipoEndereco': [this.dadosEndereco.codigoTipoEndereco == 2 ? 'entrega' : 'faturamento'],
      'descricaoReferencia': [this.dadosEndereco.descricaoReferencia],
      'nomeRespon1': [this.dadosEndereco.nomeRespon1],
      'telefoneRespon1': [this.dadosEndereco.telefoneRespon1],
      'ramalTelefonRespon1': [this.dadosEndereco.ramalTelefonRespon1],
      'nomeRespon2': [this.dadosEndereco.nomeRespon2],
      'telefoneRespon2': [this.dadosEndereco.telefoneRespon2],
      'ramalTelefonRespon2': [this.dadosEndereco.ramalTelefonRespon2],
      'nomeRespon3': [this.dadosEndereco.nomeRespon3],
      'telefoneRespon3': [this.dadosEndereco.telefoneRespon3],
      'ramalTelefonRespon3': [this.dadosEndereco.ramalTelefonRespon3],
    });

    // Na edição o CEP vem preenchido do backend e não deve ser alterado
    this.formulario.get('codigoCep')?.disable();
    this.formulario.get('codigoCidade')?.disable();
    this.formulario.get('codigoEstado')?.disable();

    if (!statusCep) {
      this.formulario.get('descricaoLogradouro')?.disable();
      this.formulario.get('codigoBairro')?.disable();
    }

    this.formulario.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.formularioChanges = true;
    });
  }

  // API FINAL para ATUALIZAR(editar) os dados do ENDEREÇO
  editarEndereco() {
    if (this.formulario.invalid) {
      return;
    }

    let objEndereco = this.formulario.getRawValue();
    objEndereco.idEndereco = this.idEndereco;
    objEndereco.idEmpresa = this.idEmpresaLogada;
    objEndereco.codigoTipoEndereco = objEndereco.codigoTipoEndereco === 'entrega' ? 2 : 1;
    this.alertLoading = true;

    this.service.updateEndereco(objEndereco).pipe(take(1)).subscribe({
      next: (response: any) => {
        this.alertLoading = false;

        if (response.status === 'OK') {
          this.alertSucesso = true;
        } else {
          this.alertError = true;
        }
      },
      error: (erro: any) => {
        console.error(erro);
        this.alertLoading = false;
        this.alertError = true;
        this.mensagemErro = CustomErrorHandler.extrairMensagem(erro, 'Erro ao editar endereço.');
      }
    });
  }

  voltarAbaEnderecos() {
    this.rota.navigate(['dados-empresa']);
    this.domUtils.aguardarElementoClique('enderecoTab', 3, 1500);
  }

  fecharAlertSucesso(evento: string) {
    this.alertSucesso = false;
    this.rota.navigate(['dados-empresa']);
    this.domUtils.aguardarElementoClique('enderecoTab', 4, 1200);
  }

  // Fechar o alerta de erro, clicando no botão OK
  fecharAlertError(evento: string) { this.alertError = false; }

  somenteNumeros(e: any) {
    const charCode = e.charCode ?? e.keyCode;

    if (charCode != 8 && charCode != 9) {
      let max = 10;
      if ((charCode < 48 || charCode > 57)||(e.target.value.length >= max)) return false;
    }
  }

}
